// ============================================
// CONTEXTO DE USUARIO — almacena usuario logueado y permisos
// Persiste en localStorage entre recargas
// ============================================

import React, { createContext, useContext, useState, useEffect } from 'react';
// PERM-2: usar las funciones nuevas de utils/permisos.js (formato JSONB real).
// puede/puedeVer/tieneAccesoTotal trabajan con claves "modulo.submodulo".
import { permisosActivos, puede as puedeFn, puedeVer as puedeVerFn, puedeAcceder as puedeAccederFn, tieneAccesoTotal } from '../utils/permisos';
import { STORAGE_KEYS, storage } from '../utils/storage';
import { API_BASE } from '../config/api';

const UsuarioContext = createContext(null);

export function UsuarioProvider({ children }) {
  // PERM-FIX (BUG #3): NO confiar en permisos cacheados. Cargamos el usuario
  // desde localStorage SIN el campo permisos para forzar refetch siempre.
  // Esto evita perpetuar un permisos={} stale entre sesiones.
  const [usuario, setUsuarioState] = useState(() => {
    const cached = storage.get(STORAGE_KEYS.USER_CONTEXT_CACHE);
    if (!cached) return null;
    const { permisos: _ignorado, ...resto } = cached;
    return resto;
  });

  // PERM-FIX (BUG #1): flag separado del objeto permisos. Antes el guard del
  // fetch usaba `if (usuario.permisos && typeof === 'object') return`, lo que
  // permitia que {} pasara como "ya cargado" y bloqueara el refetch para
  // siempre. Ahora el flag es independiente del valor del JSONB.
  const [permisosFetched, setPermisosFetched] = useState(false);

  // PERM-FIX (BUG #3): persistir cache SIN permisos para evitar perpetuar
  // estados incorrectos. El cache solo guarda identidad (id, correo, rol, etc).
  useEffect(() => {
    if (usuario) {
      const { permisos: _ignorado, ...sinPermisos } = usuario;
      storage.set(STORAGE_KEYS.USER_CONTEXT_CACHE, sinPermisos);
    } else {
      storage.remove(STORAGE_KEYS.USER_CONTEXT_CACHE);
    }
  }, [usuario]);

  // PERM-FIX (BUG #1): cuando cambia el usuario (logout/login con otro
  // correo), resetear el flag para forzar un nuevo fetch de permisos.
  useEffect(() => {
    setPermisosFetched(false);
  }, [usuario?.id]);

  // PERM-3.A+B + PERM-FIX (BUG #1): fetch de permisos JSONB al iniciar sesion.
  // El guard ahora usa permisosFetched (flag explicito), no la presencia del
  // objeto. Si el fetch falla, marcamos como intentado para no reintentar
  // infinitamente, pero NO seteamos permisos: RutaProtegida y Dashboard usaran
  // permisosFetched para distinguir "fallo el fetch" de "vacio confirmado".
  useEffect(() => {
    if (!usuario?.id) return;
    if (permisosFetched) return;
    // CONTRATISTAS-EXTERNOS: la cuenta externa no tiene matriz (su portal no la usa) y la puerta unica le daria 403.
    if (usuario.portal === 'contratista') return;
    const token = storage.get(STORAGE_KEYS.AUTH_TOKEN);
    if (!token) return;
    fetch(`${API_BASE}/api/usuarios/${usuario.id}/permisos`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => {
        if (d && d.ok) {
          setUsuarioState(prev => prev ? { ...prev, permisos: d.permisos || {} } : prev);
          setPermisosFetched(true);
        }
      })
      .catch(() => {
        // Falla silenciosa: marcar como intentado para evitar loops, pero
        // NO setear permisos. El sistema queda en estado degradado (que
        // RutaProtegida puede detectar via permisosFetched=true + sin permisos).
        setPermisosFetched(true);
      });
  }, [usuario?.id, permisosFetched]);

  // VER-COMO multi-pestana (veto logica-negocio): si OTRA pestana cambia el
  // token (inicio o fin de Ver como), esta pestana quedaria operando con una
  // identidad distinta a la que muestra en pantalla. El evento 'storage' solo
  // dispara en las pestanas QUE NO hicieron el cambio: recargamos para
  // resincronizar identidad visible con token vigente.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEYS.AUTH_TOKEN || e.key === STORAGE_KEYS.VER_COMO_META) {
        window.location.reload();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setUsuario = (u) => setUsuarioState(u);
  const cerrarSesion = () => setUsuarioState(null);

  // PERM-2: estos closures cierran sobre el usuario actual del context para que
  // los consumidores solo pasen la clave (no el usuario).
  const permisos = permisosActivos(usuario);
  const puede = (claveModulo) => puedeFn(usuario, claveModulo);
  const puedeVer = (moduloId) => puedeVerFn(usuario, moduloId);
  // FIX-GATE-FLAT: gate unico para RutaProtegida y cualquier guard de modulo.
  // Resuelve claves con punto (permiso exacto) y sin punto (seccion flat).
  const puedeAcceder = (clave) => puedeAccederFn(usuario, clave);
  const accesoTotal = tieneAccesoTotal(usuario);

  return (
    <UsuarioContext.Provider value={{
      usuario, setUsuario, cerrarSesion,
      permisos, puede, puedeVer, puedeAcceder, accesoTotal,
      permisosFetched,
    }}>
      {children}
    </UsuarioContext.Provider>
  );
}

export function useUsuario() {
  const ctx = useContext(UsuarioContext);
  if (!ctx) throw new Error('useUsuario debe usarse dentro de UsuarioProvider');
  return ctx;
}
